import { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import { always, cond, equals, T } from 'ramda';
import { isBoolean, isNilOrEmpty } from 'ramda-extension';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { PKUrl } from 'components';
import { Incognito, Minus, OpenedLetter } from 'components/atoms/CustomSVGIcon';
import { setResetRelatedSections } from 'features/views/campaignManagement/contacts/actions';
import contactDetail from 'features/views/contactDetail';
import { currency, equalOrThreeDashes, fromIsoDate, getCodelistPropById, getRoutePath } from 'utils';
import { getClientName } from 'utils/client';
import { joinString } from 'utils/string';

const { getOwnerValue } = contactDetail.utils;

const getTooltip = cond([
	[equals('NO_PRINT'), always('noPrint')],
	[equals('ANONYMOUS'), always('anonymousPrint')],
	[equals('NAMED'), always('namedPrint')],
	[T, always(null)],
]);

export const ContactsTableRow = ({
	contact = {},
	columnsPositions,
	idkAgenciesCodelist,
	idkRegionsCodelist,
	solutionStateCodelist,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const generatePrintIcon = useCallback((printType) => {
		let Icon;
		switch (printType) {
			case 'NO_PRINT':
				Icon = Minus;
				break;
			case 'ANONYMOUS':
				Icon = Incognito;
				break;
			case 'NAMED':
				Icon = OpenedLetter;
				break;
		}

		return (
			<Tooltip placement="right" title={getLabelMessage(`campaignManagement:${getTooltip(printType)}`)}>
				<IconButton
					sx={{
						fontSize: '20px',
						color: 'primary.main',
					}}
					disableRipple
				>
					<Icon />
				</IconButton>
			</Tooltip>
		);
	}, []);

	const renderColumnWithName = useCallback(
		(name) => {
			const {
				contractEndDate,
				productName,
				printType,
				solutionStateId,
				solver,
				policy = {},
				client = {},
				policyAgentSeller = {},
				campaign = {},
				isSeller,
			} = contact;

			const { communicationCode, endDate } = campaign;
			const { ccdId, businessIn, personIn, businessName, address = {} } = client;
			const { street, streetNo, zipCode, city } = address;
			const { insurancePremium } = policy;

			const policyAgentSellerName = joinString(' ', [policyAgentSeller.surname, policyAgentSeller.firstName]);
			const streetWithNumber = joinString(' ', [street, streetNo]);
			const municipality = joinString(' ', [zipCode, city]);
			const owner = getOwnerValue(solver || {}, idkAgenciesCodelist, idkRegionsCodelist, 'shortDescription');
			const solutionState = getCodelistPropById(solutionStateId, solutionStateCodelist, 'shortDescription');

			switch (name) {
				case 'client':
					const clientName = getClientName(client);
					return (
						<PKUrl.PKPartner
							muiLink
							partnerId={client.ccdId}
							campaignEndDate={endDate}
							communicationCode={communicationCode}
							unlock
						>
							{equalOrThreeDashes(clientName)}
							<br />
						</PKUrl.PKPartner>
					);
				case 'birthNumber':
					if (businessName) {
						return equalOrThreeDashes(businessIn);
					} else {
						return equalOrThreeDashes(personIn);
					}
				case 'address':
					return (
						<>
							{equalOrThreeDashes(streetWithNumber)}
							<br />
							{equalOrThreeDashes(municipality)}
						</>
					);
				case 'policyNo':
					return (
						<PKUrl.PKPolicy
							muiLink
							partnerId={ccdId}
							policyNo={policy.policyNo}
							systemId={policy.systemId}
							communicationCode={communicationCode}
							campaignEndDate={endDate}
							unlock
						>
							{equalOrThreeDashes(policy.policyNo)}
							<br />
						</PKUrl.PKPolicy>
					);
				case 'assignedTo':
					return (
						<>
							{equalOrThreeDashes(owner)}
							<br />
						</>
					);
				case 'status':
					return equalOrThreeDashes(solutionState);
				case 'policyAgent':
					return (
						<>
							{equalOrThreeDashes(policyAgentSellerName)}
							<br />
						</>
					);
				case 'policyFinishDate':
					return (
						<>
							{equalOrThreeDashes(fromIsoDate(contractEndDate))}
							<br />
						</>
					);
				case 'product':
					return (
						<>
							{equalOrThreeDashes(productName)}
							<br />
						</>
					);
				case 'insurancePremium':
					return (
						<>
							{currency(insurancePremium)}
							<br />
						</>
					);
				case 'isSeller':
					return (
						<>
							{equalOrThreeDashes(isBoolean(isSeller) ? (isSeller ? 1 : 0) : isSeller)}
							<br />
						</>
					);
				case 'print':
					return !isNilOrEmpty(printType) && printType !== 'UNKNOWN' && generatePrintIcon(printType);
				default:
					return name;
			}
		},
		[contact, idkAgenciesCodelist, idkRegionsCodelist, solutionStateCodelist, generatePrintIcon],
	);

	const handleRedirectToDetail = useCallback(
		(contactId) => (event) => {
			dispatch(setResetRelatedSections(false));
			event.preventDefault();
			event.stopPropagation();

			const { pathname, state } = getRoutePath('contactDetail', { contactId }, true);
			navigate(pathname, { state });
		},
		[dispatch, navigate],
	);

	return (
		<Fragment>
			{columnsPositions.map((cell, i) => (
				<TableCell key={i}>
					{cell.map((value, i) => (
						<Fragment key={i}>{renderColumnWithName(value)}</Fragment>
					))}
				</TableCell>
			))}
			<TableCell>
				<Link underline="none" onClick={handleRedirectToDetail(contact?.id)}>
					<Box sx={{ color: 'primary.main', fontWeight: 'bold' }}>{getLabelMessage('common:detail')}</Box>
				</Link>
			</TableCell>
		</Fragment>
	);
};

ContactsTableRow.propTypes = {
	columnsPositions: PropTypes.array,
	contact: PropTypes.object,
	idkAgenciesCodelist: PropTypes.array,
	idkRegionsCodelist: PropTypes.array,
	solutionStateCodelist: PropTypes.array,
};

export default ContactsTableRow;
